import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Save, Building2, MapPin, Zap, FileText, CheckCircle, XCircle, Plus,
  Loader, AlertCircle, Database, RefreshCw, Table, Settings2, Activity, Cpu, Layers
} from 'lucide-react';
import {
  TableSchemaModal,
  DEFAULT_LIVEDAYCONS_COLUMNS,
  DEFAULT_LIVESTRINGINV_COLUMNS,
  TableColumnDef
} from './TableSchemaModal';
import backendApi from '../../services/backendApi';
import { clearSiteCache } from '../../components/PrivateHeader';
import { useAuth } from '../../context/AuthContext';
import {
  debounce,
  validateSiteCode,
  validateNumber,
  getFieldStatusClass
} from '../../utils/validation';
import {
  ModuleCatalogItem,
  loadModuleCatalog,
  getModuleSelectionMap,
  applySiteRolePolicies,
} from '../../utils/moduleAccessAdmin';


interface ValidationState {
  isValid: boolean;
  message: string;
  isChecking: boolean;
}

const SiteCreate: React.FC = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [existingSites, setExistingSites] = useState<any[]>([]);
  const [companySubscription, setCompanySubscription] = useState<any>({
    max_sites: null,
    max_users: null,
  });
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');

  const [formData, setFormData] = useState({
    site_name: '',
    site_code: '',
    site_type: '',
    location: '',
    capacity_mw: '',
    capacity_mwp: '',
    description: '',
    is_active: true,
    // New fields
    local_time_zone: '',
    timezone_preference: '',
    latitude: '',
    longitude: '',
    altitude: '',
    ppa_tariff: '',
    plant_code: '',
    currency: 'INR',
    plant_commissioning_date: '',
    data_resolution: '',
    site_manager_name: '',
    site_manager_email: '',
    site_manager_phone_no: '',
    company_id: '',
    database_name: '',
  });

  const [isDbManuallyEdited, setIsDbManuallyEdited] = useState(false);

  // Telemetry Table Schema Customization States
  const [livedayconsColumns, setLivedayconsColumns] = useState<TableColumnDef[]>(DEFAULT_LIVEDAYCONS_COLUMNS);
  const [livestringinvColumns, setLivestringinvColumns] = useState<TableColumnDef[]>(DEFAULT_LIVESTRINGINV_COLUMNS);
  const [activeSchemaModal, setActiveSchemaModal] = useState<'livedaycons' | 'livestringinv' | null>(null);

  const { user } = useAuth();
  const roleName = String((user as any)?.roleName || user?.role || '').toLowerCase();
  const isSystemAdmin =
    !!user?.isGlobalAdmin ||
    !!(user as any)?.is_superuser ||
    !!(user as any)?.is_super_admin ||
    ['system_admin', 'super_admin'].includes(roleName);

  const isHoAdmin = roleName === 'ho_admin' || roleName === 'company_admin' || (roleName === 'admin' && !user?.siteId);
  const isSiteAdmin = roleName === 'site_admin' || (roleName === 'admin' && !!user?.siteId);

  // Site Creation allowed for Super Admin and HO Admin, but NOT Site Admin
  const canCreateSite = isSystemAdmin || (isHoAdmin && !isSiteAdmin);
  const isSystemLevelAdmin = isSystemAdmin; // Keeping name for backward compatibility where used for company selection visibility
  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [moduleCatalog, setModuleCatalog] = useState<ModuleCatalogItem[]>([]);
  const [companyAllowedModuleKeys, setCompanyAllowedModuleKeys] = useState<string[]>([]);
  const [siteModuleSelection, setSiteModuleSelection] = useState<Record<string, boolean>>({});
  const [applySiteModulePolicy, setApplySiteModulePolicy] = useState(true);
  const [sitePolicyRoleIds, setSitePolicyRoleIds] = useState<number[]>([]);

  // Dropdown values from database
  const [dropdowns, setDropdowns] = useState<Record<string, Array<{ value: string; label: string }>>>({
    site_types: [],
    plant_codes: [],
    data_resolutions: [],
    currencies: [],
    timezones: [],
  });


  // Real-time validation states
  const [siteNameValidation, setSiteNameValidation] = useState<ValidationState>({
    isValid: false,
    message: '',
    isChecking: false
  });

  const [siteCodeValidation, setSiteCodeValidation] = useState<ValidationState>({
    isValid: false,
    message: '',
    isChecking: false
  });

  const [capacityValidation, setCapacityValidation] = useState<ValidationState>({
    isValid: false,
    message: '',
    isChecking: false
  });

  // Authorization guard
  useEffect(() => {
    if (user && !canCreateSite) {
      alert('Unauthorized: Only Super Admin and HO Admin can create sites.');
      navigate('/admin/sites');
    }
  }, [user, canCreateSite, navigate]);

  useEffect(() => {
    if (canCreateSite) {
      fetchExistingSites();
      fetchCompanies();
      fetchDropdowns();
    }
  }, [user, isSystemLevelAdmin, canCreateSite]);

  const fetchDropdowns = async () => {
    try {
      const response = await backendApi.getDropdowns();
      if (response.success && response.dropdowns) {
        setDropdowns({
          site_types: response.dropdowns.site_types || [],
          plant_codes: response.dropdowns.plant_codes || [],
          data_resolutions: response.dropdowns.data_resolutions || [],
          currencies: response.dropdowns.currencies || [],
          timezones: response.dropdowns.timezones || [],
        });
      }
    } catch (error) {
      console.error('Failed to load dropdowns:', error);
      // Fallback to empty dropdowns - can still use form
    }
  };

  const availableSiteModules = moduleCatalog.filter(
    (module) => companyAllowedModuleKeys.includes(module.module_key)
  );

  const syncSelectionToAllowedKeys = (allowedKeys: string[]) => {
    setSiteModuleSelection((prev) => {
      const next: Record<string, boolean> = {};
      moduleCatalog.forEach((module) => {
        if (allowedKeys.includes(module.module_key)) {
          next[module.module_key] = prev[module.module_key] !== undefined ? prev[module.module_key] : true;
        }
      });
      return next;
    });
  };

  const loadCompanyModuleAccess = async (companyId: string | null) => {
    if (!companyId) {
      setCompanyAllowedModuleKeys([]);
      syncSelectionToAllowedKeys([]);
      return;
    }

    try {
      const response = await backendApi.getNavigationSubscriptions(companyId);
      if (!response.success) {
        setCompanyAllowedModuleKeys([]);
        syncSelectionToAllowedKeys([]);
        return;
      }

      const activeKeys: string[] = Array.from(new Set<string>(
        (response.subscriptions || [])
          .filter((item: any) => String(item?.status || '').toLowerCase() === 'active')
          .map((item: any) => String(item?.module_key || '').trim())
          .filter((key: string) => key.length > 0)
      ));

      setCompanyAllowedModuleKeys(activeKeys);
      syncSelectionToAllowedKeys(activeKeys);
    } catch (error) {
      console.error('Failed to load company module subscriptions:', error);
      setCompanyAllowedModuleKeys([]);
      syncSelectionToAllowedKeys([]);
    }
  };

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [modules, rolesResponse] = await Promise.all([
          loadModuleCatalog(backendApi),
          backendApi.getRoles(),
        ]);

        setModuleCatalog(modules);
        setSiteModuleSelection(getModuleSelectionMap(modules));

        const roles = Array.isArray(rolesResponse?.roles) ? rolesResponse.roles : [];
        const roleIds = roles
          .filter((role: any) => ['site_admin', 'general_user'].includes(String(role?.role_name || '').toLowerCase()))
          .map((role: any) => Number(role?.role_id))
          .filter((value: number) => Number.isFinite(value) && value > 0);
        setSitePolicyRoleIds(roleIds);
      } catch (error) {
        console.error('Failed to load site module metadata:', error);
      }
    };

    loadMeta();
  }, []);

  // Auto-set company_id for non-super admins (Super Admin picks manually)
  useEffect(() => {
    if (!isSystemLevelAdmin && user && user.companyId) {
const companyIdStr = String(user.companyId);
      setFormData(prev => ({
        ...prev,
        company_id: companyIdStr
      }));
      void loadCompanyModuleAccess(companyIdStr);
      void fetchCompanySubscription(companyIdStr);
    }
  }, [user, isSystemLevelAdmin]);

  useEffect(() => {
    if (isSystemLevelAdmin && formData.company_id) {
      void loadCompanyModuleAccess(formData.company_id);
      void fetchCompanySubscription(formData.company_id);
    }
  }, [isSystemLevelAdmin, formData.company_id]);

  const fetchCompanySubscription = async (companyId: string) => {
    try {
      const companiesResponse = await backendApi.getCompanies();
      if (companiesResponse.success && companiesResponse.companies) {
        const company = companiesResponse.companies.find(
          (c: any) => String(c.company_id) === String(companyId)
        );
        if (company) {
          setCompanySubscription({
            max_sites: company.max_sites || 10,
            max_users: company.max_users || 10,
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch company subscription:', error);
    }
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await backendApi.getCompanies();
      if (response.success && response.companies && response.companies.length > 0) {
        // Ensure unique companies to prevent key duplication errors
        const unique = Array.from(new Map(response.companies.map((item: any) => [item.company_id, item])).values()) as any[];
        // Only add fallback entry for non-super-admin users whose companyId isn't in the API response
        if (!isSystemLevelAdmin && user?.companyId && !unique.find((c: any) => String(c.company_id) === String(user.companyId))) {
          unique.unshift({
            company_id: user.companyId,
            company_name: user.company || 'Assigned Company',
            company_code: String(user.companyId)
          });
        }
        setCompanies(unique);
      } else if (user?.companyId) {
        // API returned empty or failed — use fallback from user context
        setCompanies([{
          company_id: user.companyId,
          company_name: user.company || 'Assigned Company',
          company_code: String(user.companyId)
        }]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      if (user?.companyId) {
        setCompanies([{
          company_id: user.companyId,
          company_name: user.company || 'Assigned Company',
          company_code: String(user.companyId)
        }]);
      }
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchExistingSites = async () => {
    try {
      const response = await backendApi.getSites(false);
      if (response.success && response.sites) {
        setExistingSites(response.sites);

        // Auto-assign incremental Site Code
        const siteCodes = response.sites
          .map((s: any) => s.site_code)
          .filter((code: string) => /^AXON\d+$/.test(code))
          .map((code: string) => parseInt(code.substring(4)));

        const nextNum = siteCodes.length > 0 ? Math.max(...siteCodes) + 1 : 1;
        const nextCode = `AXON${String(nextNum).padStart(3, '0')}`;
        setFormData(prev => ({ ...prev, site_code: nextCode }));
        setSiteCodeValidation({
          isValid: true,
          message: 'Incremental code assigned',
          isChecking: false
        });
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  // Debounced site name uniqueness check
  const checkSiteNameAvailability = useCallback(
    debounce((siteName: string) => {
      if (!siteName || siteName.trim().length < 2) {
        setSiteNameValidation({ isValid: false, message: '', isChecking: false });
        return;
      }

      setSiteNameValidation(prev => ({ ...prev, isChecking: true }));

      // Simulate async check
      setTimeout(() => {
        const trimmedName = siteName.trim();
        const exists = existingSites.some(
          site => site.site_name.toLowerCase() === trimmedName.toLowerCase()
        );

        setSiteNameValidation({
          isValid: !exists,
          message: exists
            ? 'A site with this name already exists'
            : 'Site name is available',
          isChecking: false
        });
      }, 300);
    }, 600),
    [existingSites]
  );

  // Debounced site code format and uniqueness check
  const checkSiteCodeAvailability = useCallback(
    debounce((siteCode: string) => {
      if (!siteCode) {
        setSiteCodeValidation({ isValid: false, message: '', isChecking: false });
        return;
      }

      const upperCode = siteCode.toUpperCase();
      const formatCheck = validateSiteCode(upperCode);

      if (!formatCheck.valid) {
        setSiteCodeValidation({
          isValid: false,
          message: formatCheck.message,
          isChecking: false
        });
        return;
      }

      setSiteCodeValidation(prev => ({ ...prev, isChecking: true }));

      // Check uniqueness
      setTimeout(() => {
        const exists = existingSites.some(
          site => site.site_code.toUpperCase() === upperCode
        );

        setSiteCodeValidation({
          isValid: !exists,
          message: exists
            ? 'A site with this code already exists'
            : 'Site code is available',
          isChecking: false
        });
      }, 300);
    }, 600),
    [existingSites]
  );

  // Debounced capacity validation
  const validateCapacity = useCallback(
    debounce((capacity: string) => {
      if (!capacity) {
        setCapacityValidation({ isValid: true, message: '', isChecking: false });
        return;
      }

      setCapacityValidation(prev => ({ ...prev, isChecking: true }));

      setTimeout(() => {
        const numCheck = validateNumber(capacity, 0);
        setCapacityValidation({
          isValid: numCheck.valid,
          message: numCheck.message,
          isChecking: false
        });
      }, 200);
    }, 500),
    []
  );

  // Helper to compute standard telemetry database name
  const computeDatabaseName = (companyId: string, siteName: string, siteCode: string) => {
    const comp = companies.find((c: any) => String(c.company_id) === String(companyId));
    const compName = comp ? (comp.company_name || comp.company_code) : '';
    const cleanComp = (compName || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const cleanSite = (siteName || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const cleanCode = (siteCode || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

    if (cleanComp && cleanSite) {
      return `axonmetric_${cleanComp}${cleanSite}`.substring(0, 128);
    } else if (cleanSite) {
      return `axonmetric_${cleanSite}`.substring(0, 128);
    } else if (cleanCode) {
      return `axonmetric_${cleanCode}`.substring(0, 128);
    }
    return '';
  };

  // Handle site name change
  const handleSiteNameChange = (value: string) => {
    const nextDbName = !isDbManuallyEdited 
      ? computeDatabaseName(formData.company_id, value, formData.site_code) 
      : formData.database_name;

    setFormData(prev => ({
      ...prev,
      site_name: value,
      database_name: nextDbName
    }));
    setErrors({ ...errors, site_name: '' });

    if (value.trim()) {
      checkSiteNameAvailability(value);
    } else {
      setSiteNameValidation({ isValid: false, message: '', isChecking: false });
    }
  };

  // Handle site code change
  const handleSiteCodeChange = (value: string) => {
    const upperValue = value.toUpperCase().replace(/\s/g, '');
    setFormData({ ...formData, site_code: upperValue });
    setErrors({ ...errors, site_code: '' });

    if (upperValue) {
      checkSiteCodeAvailability(upperValue);
    } else {
      setSiteCodeValidation({ isValid: false, message: '', isChecking: false });
    }
  };

  // Handle capacity change
  const handleCapacityChange = (value: string) => {
    setFormData({ ...formData, capacity_mw: value });
    setErrors({ ...errors, capacity_mw: '' });

    if (value) {
      validateCapacity(value);
    } else {
      setCapacityValidation({ isValid: true, message: '', isChecking: false });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.site_name.trim()) {
      newErrors.site_name = 'Site name is required';
    } else if (!siteNameValidation.isValid && !siteNameValidation.isChecking) {
      newErrors.site_name = siteNameValidation.message || 'Site name is not available';
    }

    if (!formData.site_code.trim()) {
      newErrors.site_code = 'Site code is required';
    } else if (!siteCodeValidation.isValid && !siteCodeValidation.isChecking) {
      newErrors.site_code = siteCodeValidation.message || 'Site code is not valid';
    }

    if (formData.capacity_mw && !capacityValidation.isValid && !capacityValidation.isChecking) {
      newErrors.capacity_mw = capacityValidation.message || 'Invalid capacity value';
    }

    if (!formData.company_id) {
      newErrors.company_id = 'Company selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    // Check if max_sites limit is reached
    if (companySubscription.max_sites !== null) {
      const currentSiteCount = existingSites.filter(
        (s: any) => String(s.company_id) === String(formData.company_id)
      ).length;
      
      if (currentSiteCount >= companySubscription.max_sites) {
        setLimitMessage(
          `Maximum sites limit (${companySubscription.max_sites}) has been reached for this company. Please contact your system administrator to increase the limit.`
        );
        setShowLimitAlert(true);
        return;
      }
    }

    setSaving(true);
    try {
      const extractIframeSrc = (input: string) => {
        if (!input) return "";
        const trimmed = input.trim();
        if (trimmed.startsWith('<iframe')) {
          const match = trimmed.match(/src=["']([^"']+)["']/i);
          return match ? match[1] : trimmed;
        }
        return trimmed;
      };

      const processEmbeds = (embeds: Array<{ name: string; url: string }>) => {
        const list = embeds
          .map(e => ({
            name: e.name.trim(),
            url: extractIframeSrc(e.url)
          }))
          .filter(e => e.url);
        return list.length > 0 ? JSON.stringify(list) : null;
      };

      const createData: any = {
        site_name: formData.site_name.trim(),
        site_code: formData.site_code.trim().toUpperCase(),
        site_type: formData.site_type.trim() || null,
        location: formData.location.trim() || null,
        description: formData.description.trim() || null,
        is_active: formData.is_active,
        // New fields
        local_time_zone: formData.local_time_zone.trim() || formData.timezone_preference.trim() || null,
        timezone_preference: formData.timezone_preference.trim() || formData.local_time_zone.trim() || null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        altitude: formData.altitude ? parseFloat(formData.altitude) : null,
        ppa_tariff: formData.ppa_tariff ? parseFloat(formData.ppa_tariff) : null,
        plant_code: formData.plant_code ? parseInt(formData.plant_code) : null,
        currency: formData.currency.trim() || 'INR',
        plant_commissioning_date: formData.plant_commissioning_date || null,
        data_resolution: formData.data_resolution.trim() || null,
        site_manager_name: formData.site_manager_name.trim() || null,
        site_manager_email: formData.site_manager_email.trim() || null,
        site_manager_phone_no: formData.site_manager_phone_no.trim() || null,
        company_id: formData.company_id || null,
        database_name: formData.database_name.trim() || undefined,
        livedaycons_columns: livedayconsColumns,
        livestringinv_columns: livestringinvColumns,
      };


      if (formData.capacity_mw) {
        createData.capacity_mw = parseFloat(formData.capacity_mw);
      }

      if (formData.capacity_mwp) {
        createData.capacity_mwp = parseFloat(formData.capacity_mwp);
      }

      const response = await backendApi.createSite(createData);

      if (response.success) {
        const createdSiteId = Number(response?.site?.site_id);
        const createdCompanyId = Number(formData.company_id);
        const constrainedSiteSelection = availableSiteModules.reduce((acc, module) => {
          acc[module.module_key] = !!siteModuleSelection[module.module_key];
          return acc;
        }, {} as Record<string, boolean>);
        if (
          applySiteModulePolicy &&
          createdSiteId > 0 &&
          createdCompanyId > 0 &&
          sitePolicyRoleIds.length > 0 &&
          Object.keys(constrainedSiteSelection).length > 0
        ) {
          await applySiteRolePolicies(backendApi, {
            companyId: createdCompanyId,
            siteId: createdSiteId,
            moduleSelections: constrainedSiteSelection,
            roleIds: sitePolicyRoleIds,
          });
        }

        // Clear site cache to force refresh of map coordinates in PrivateHeader
        if (formData.site_code) {
          clearSiteCache(formData.site_code.toUpperCase());
        }
        // Also clear all site caches to be safe
        clearSiteCache();
        navigate('/admin/sites');
      } else {
        const errorMsg = response.error || 'Failed to create site';
        const lower = errorMsg.toLowerCase();
        if (lower.includes('name already exists') || lower.includes('site_name')) {
          setErrors(prev => ({ ...prev, site_name: 'A site with this name already exists' }));
        } else if (lower.includes('code already exists') || lower.includes('site_code')) {
          setErrors(prev => ({ ...prev, site_code: 'A site with this code already exists' }));
        } else {
          setApiError(errorMsg);
        }
      }
    } catch (error: any) {
      console.error('Error creating site:', error);
      setApiError(error.message || 'Failed to create site');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
      {showLimitAlert && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        >
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Limit Reached</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">{limitMessage}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowLimitAlert(false)}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </motion.div>
      )}
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.section
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-hero p-4 sm:p-4 lg:p-5"
        >
          <div className="admin-toolbar">
            <div className="space-y-2">
              <p className="admin-eyebrow">Site onboarding</p>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 rounded-[16px] bg-[linear-gradient(135deg,#0084CF,#1C4D88)] items-center justify-center text-white shadow-[0_12px_20px_rgba(0,132,207,0.2)]">
                  <Plus size={22} />
                </div>
                <div>
                  <h1 className="admin-page-title">Create a new operational site with technical and policy setup.</h1>
                  <p className="admin-page-subtitle mt-2">The form has been adapted to the new admin layout and widened for better screen-fit on desktop and mobile.</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate('/admin/sites')}
              className="admin-button admin-button-ghost"
            >
              <ArrowLeft size={16} />
              Back to sites
            </button>
          </div>
        </motion.section>

        {apiError && (
          <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 shadow-sm animate-in fade-in duration-200">
            <div className="flex items-center gap-3">
              <AlertCircle size={20} className="text-red-500 shrink-0" />
              <span className="text-sm font-medium">{apiError}</span>
            </div>
            <button
              type="button"
              onClick={() => setApiError(null)}
              className="text-red-500 hover:text-red-700 text-sm font-semibold ml-4"
            >
              Dismiss
            </button>
          </div>
        )}

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-section"
        >
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Site creation form</h2>
              <p className="admin-section-copy">Configure company mapping, location, capacity, site metadata, and module policy in one structured form.</p>
            </div>
          </div>
          <div className="admin-section-body !p-4 sm:!p-5 lg:!p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Selection */}
            <div className="bg-[#F2F8FC]/50 p-4 rounded-lg border border-[#E3F0F9] mb-8">
              <label className="admin-form-label">
                Assigned Company <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={formData.company_id}
                  onChange={(e) => {
                    const nextCompanyId = e.target.value;
                    const nextDb = !isDbManuallyEdited
                      ? computeDatabaseName(nextCompanyId, formData.site_name, formData.site_code)
                      : formData.database_name;
                    setFormData(prev => ({ ...prev, company_id: nextCompanyId, database_name: nextDb }));
                    setErrors({ ...errors, company_id: '' });
                    void loadCompanyModuleAccess(nextCompanyId || null);
                  }}
                  disabled={!isSystemLevelAdmin && !!user?.companyId}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] outline-none transition-all appearance-none bg-white ${errors.company_id ? 'border-red-500' : 'border-[#C2DFF1]'
                    } ${!isSystemLevelAdmin && !!user?.companyId ? 'bg-[#E3F0F9]/60 cursor-not-allowed' : ''}`}
                >
                  <option value="">Select a company</option>
                  {companies.map((company) => (
                    <option key={company.company_id} value={company.company_id}>
                      {company.company_name} ({company.company_code})
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#0084CF]">
                  {loadingCompanies ? <Loader size={18} className="animate-spin" /> :
                    <ArrowLeft size={18} className="rotate-[270deg]" />}
                </div>
              </div>
              {errors.company_id && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle size={14} />
                  {errors.company_id}
                </p>
              )}
              <p className="text-slate-500 text-xs mt-2">
                Select the company this site belongs to. This determines database isolation.
              </p>
            </div>

            <div className="bg-white border border-[#E3F0F9] rounded-lg p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Site Module Access</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Configure module access for Site Admin and General User at this site.</p>
                </div>
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={applySiteModulePolicy}
                    onChange={(e) => setApplySiteModulePolicy(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#0066A3] focus:ring-[#0084CF]"
                  />
                  Apply on create
                </label>
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => {
                    const allEnabled: Record<string, boolean> = {};
                    availableSiteModules.forEach((module) => {
                      allEnabled[module.module_key] = true;
                    });
                    setSiteModuleSelection((prev) => ({ ...prev, ...allEnabled }));
                  }}
                  className="px-2.5 py-1 text-xs font-semibold border border-[#0066A3] text-[#1C4D88] rounded-md hover:bg-[#F2F8FC]"
                >
                  Enable All
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const allDisabled: Record<string, boolean> = {};
                    availableSiteModules.forEach((module) => {
                      allDisabled[module.module_key] = false;
                    });
                    setSiteModuleSelection((prev) => ({ ...prev, ...allDisabled }));
                  }}
                  className="px-2.5 py-1 text-xs font-semibold border border-red-500 text-red-600 rounded-md hover:bg-red-50"
                >
                  Disable All
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {availableSiteModules.map((module) => (
                  <label key={module.module_key} className="flex items-center justify-between gap-2 border border-gray-200 rounded-md px-3 py-2 bg-gray-50/70 hover:bg-gray-100/80 transition-colors cursor-pointer">
                    <span className="text-xs font-semibold text-slate-800">{module.module_name}</span>
                    <input
                      type="checkbox"
                      checked={!!siteModuleSelection[module.module_key]}
                      onChange={(e) => {
                        setSiteModuleSelection((prev) => ({
                          ...prev,
                          [module.module_key]: e.target.checked,
                        }));
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-[#0066A3] focus:ring-[#0084CF]"
                    />
                  </label>
                ))}
              </div>
              {availableSiteModules.length === 0 && (
                <p className="text-xs text-amber-700 mt-2">No modules are currently assigned to this company. Assign modules at company level first.</p>
              )}
            </div>

            {/* Site Name */}
            <div className="space-y-2">
              <label className="admin-form-label">
                Site Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={formData.site_name}
                  onChange={(e) => handleSiteNameChange(e.target.value)}
                  className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 ${errors.site_name
                    ? 'border-red-500'
                    : siteNameValidation.isValid && formData.site_name
                      ? 'border-green-500'
                      : siteNameValidation.isChecking
                        ? 'border-[#0084CF]'
                        : 'border-gray-300'
                    }`}
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  placeholder=""
                  maxLength={100}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {siteNameValidation.isChecking && (
                    <Loader size={18} className="text-[#0084CF] animate-spin" />
                  )}
                  {!siteNameValidation.isChecking && formData.site_name && (
                    siteNameValidation.isValid ? (
                      <CheckCircle size={18} className="text-green-500" />
                    ) : siteNameValidation.message ? (
                      <XCircle size={18} className="text-red-500" />
                    ) : null
                  )}
                </div>
              </div>
              <div className="mt-1">
                {errors.site_name && (
                  <p className="text-red-500 text-sm flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.site_name}
                  </p>
                )}
                {!errors.site_name && siteNameValidation.message && (
                  <p className={`text-sm flex items-center gap-1 ${siteNameValidation.isValid ? 'text-green-600' : 'text-red-500'
                    }`}>
                    {siteNameValidation.isValid ? (
                      <CheckCircle size={14} />
                    ) : (
                      <XCircle size={14} />
                    )}
                    {siteNameValidation.message}
                  </p>
                )}
                {formData.site_name && (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.site_name.length}/100 characters
                  </p>
                )}
              </div>
            </div>

            {/* Site Code */}
            <div>
              <label className="admin-form-label">
                Site Code <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={formData.site_code}
                  onChange={(e) => handleSiteCodeChange(e.target.value)}
                  className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 uppercase ${errors.site_code
                    ? 'border-red-500'
                    : siteCodeValidation.isValid && formData.site_code
                      ? 'border-green-500'
                      : siteCodeValidation.isChecking
                        ? 'border-[#0084CF]'
                        : 'border-gray-300'
                    }`}
                  style={{ color: '#111827', backgroundColor: formData.site_code.startsWith('AXON') ? '#F0F7FF' : '#ffffff' }}
                  placeholder=""
                  maxLength={50}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {siteCodeValidation.isChecking && (
                    <Loader size={18} className="text-[#0084CF] animate-spin" />
                  )}
                  {!siteCodeValidation.isChecking && formData.site_code && (
                    siteCodeValidation.isValid ? (
                      <CheckCircle size={18} className="text-green-500" />
                    ) : siteCodeValidation.message ? (
                      <XCircle size={18} className="text-red-500" />
                    ) : null
                  )}
                </div>
              </div>
              <div className="mt-1">
                {errors.site_code && (
                  <p className="text-red-500 text-sm flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.site_code}
                  </p>
                )}
                {!errors.site_code && siteCodeValidation.message && (
                  <p className={`text-sm flex items-center gap-1 ${siteCodeValidation.isValid ? 'text-green-600' : 'text-red-500'
                    }`}>
                    {siteCodeValidation.isValid ? (
                      <CheckCircle size={14} />
                    ) : (
                      <XCircle size={14} />
                    )}
                    {siteCodeValidation.message}
                  </p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Must be unique and contain only uppercase letters and numbers
                </p>
                {formData.site_code && (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.site_code.length}/50 characters
                  </p>
                )}
              </div>
            </div>

            {/* Dedicated Site Telemetry Database Configuration Card */}
            <div className="bg-gradient-to-r from-[#F2F8FC]/70 to-[#F2F8FC]/50 p-4 sm:p-5 rounded-xl border border-[#C2DFF1]/80 shadow-sm space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#0066A3] text-white flex items-center justify-center shadow-sm">
                    <Database size={18} />
                  </div>
                  <div>
                    <label className="admin-form-label !mb-0">
                      Site Telemetry Database <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-gray-600">
                      Dedicated SQL Server database for this site's solar telemetry, inverter, and live metrics.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const autoDb = computeDatabaseName(formData.company_id, formData.site_name, formData.site_code);
                    setFormData(prev => ({ ...prev, database_name: autoDb }));
                    setIsDbManuallyEdited(false);
                  }}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-md border border-[#8AC4E6] bg-white text-[#1C4D88] hover:bg-[#F2F8FC] shadow-xs transition-all cursor-pointer"
                  title="Auto-generate database name from company and site" aria-label="Auto-generate database name from company and site"
                >
                  <RefreshCw size={13} />
                  Auto-Generate
                </button>
              </div>

              <div className="relative">
                <Database className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={formData.database_name}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, database_name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }));
                    setIsDbManuallyEdited(true);
                  }}
                  className="w-full pl-10 pr-4 py-2.5 border border-[#C2DFF1] rounded-lg focus:ring-2 focus:ring-[#0084CF] bg-white text-gray-900 font-mono text-sm placeholder-gray-400"
                  placeholder="e.g. axonmetric_dattainfradevikotbhakrani"
                  maxLength={128}
                />
              </div>

              <div className="flex items-start gap-2 bg-[#E3F0F9]/60 rounded-lg p-2.5 text-xs text-[#0D2747]">
                <CheckCircle size={15} className="text-[#0066A3] shrink-0 mt-0.5" />
                <span>
                  <strong>Hand-to-Hand Provisioning:</strong> This database will be created automatically on SQL Server upon site creation, pre-configured with standard telemetry tables (<code>axonmetric_livedaycons</code> and <code>axonmetric_livestringinv</code>) and performance indexes.
                </span>
              </div>

              {/* Table Schema Customization Cards */}
              <div className="pt-2 border-t border-[#C2DFF1]/70 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                    <Table size={14} className="text-[#0066A3]" />
                    Configure Telemetry Table Schemas
                  </span>
                  <span className="text-[11px] text-gray-500">
                    Default sample columns pre-loaded • Click below to customize
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Table 1: livedaycons */}
                  <div className="bg-white border border-[#C2DFF1] hover:border-[#3EA3DA] rounded-xl p-3.5 shadow-xs transition-all flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-[#E3F0F9] text-[#1C4D88] flex items-center justify-center">
                            <Activity size={16} />
                          </div>
                          <span className="text-xs font-bold text-gray-900">
                            Plant Day Cons
                          </span>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#F2F8FC] text-[#1C4D88] border border-[#C2DFF1]">
                          {livedayconsColumns.length} Columns
                        </span>
                      </div>
                      <p className="text-[11px] font-mono text-[#1C4D88]/80 mb-1 font-semibold">
                        dbo.axonmetric_livedaycons
                      </p>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Plant power, weather radiation (GIR/GTI), grid energy (MWh), and site KPIs.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setActiveSchemaModal('livedaycons')}
                      className="mt-3 w-full py-2 px-3 rounded-lg text-xs font-semibold bg-[#F2F8FC] hover:bg-[#0066A3] text-[#1C4D88] hover:text-white border border-[#C2DFF1] hover:border-[#0066A3] flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs"
                    >
                      <Settings2 size={14} />
                      Customize Table Schema ({livedayconsColumns.length})
                    </button>
                  </div>

                  {/* Table 2: livestringinv */}
                  <div className="bg-white border border-[#C2DFF1] hover:border-[#3EA3DA] rounded-xl p-3.5 shadow-xs transition-all flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-[#E3F0F9] text-[#1C4D88] flex items-center justify-center">
                            <Cpu size={16} />
                          </div>
                          <span className="text-xs font-bold text-gray-900">
                            String Inverters
                          </span>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#F2F8FC] text-[#1C4D88] border border-[#C2DFF1]">
                          {livestringinvColumns.length} Columns
                        </span>
                      </div>
                      <p className="text-[11px] font-mono text-[#1C4D88]/80 mb-1 font-semibold">
                        dbo.axonmetric_livestringinv
                      </p>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Inverter-level telemetry, DC voltage/current, AC active power, efficiency, and PR.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setActiveSchemaModal('livestringinv')}
                      className="mt-3 w-full py-2 px-3 rounded-lg text-xs font-semibold bg-[#F2F8FC] hover:bg-[#0066A3] text-[#1C4D88] hover:text-white border border-[#C2DFF1] hover:border-[#0066A3] flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs"
                    >
                      <Settings2 size={14} />
                      Customize Table Schema ({livestringinvColumns.length})
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Site Type */}
              <div>
                <label className="admin-form-label">
                  Site Type
                </label>
                <select
                  value={formData.site_type}
                  onChange={(e) => {
                    setFormData({ ...formData, site_type: e.target.value });
                    setErrors({ ...errors, site_type: '' });
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                >
                  <option value="">Select Site Type</option>
                  {dropdowns.site_types.length > 0 ? dropdowns.site_types.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  )) : (
                    <>
                      <option value="Solar">Solar</option>
                      <option value="Wind">Wind</option>
                      <option value="Hybrid">Hybrid</option>
                    </>
                  )}
                </select>
              </div>

              {/* Capacity MW and MWp */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="admin-form-label">
                    Capacity (MW)
                  </label>
                  <div className="relative">
                    <Zap className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.capacity_mw}
                      onChange={(e) => handleCapacityChange(e.target.value)}
                      className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 ${errors.capacity_mw
                        ? 'border-red-500'
                        : capacityValidation.isValid && formData.capacity_mw
                          ? 'border-green-500'
                          : capacityValidation.isChecking
                            ? 'border-[#0084CF]'
                            : 'border-gray-300'
                        }`}
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                      placeholder=""
                    />
                  </div>
                  {errors.capacity_mw && <p className="text-red-500 text-sm mt-1">{errors.capacity_mw}</p>}
                </div>

                <div>
                  <label className="admin-form-label">
                    Capacity (MWp) - DC
                  </label>
                  <div className="relative">
                    <Zap className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.capacity_mwp}
                      onChange={(e) => setFormData({ ...formData, capacity_mwp: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                      placeholder=""
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="admin-form-label">
                Location
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => {
                    setFormData({ ...formData, location: e.target.value });
                    setErrors({ ...errors, location: '' });
                  }}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  placeholder=""
                  maxLength={200}
                />
              </div>
            </div>


            {/* Description */}
            <div>
              <label className="admin-form-label">
                Description
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-4 text-gray-400" size={20} />
                <textarea
                  value={formData.description}
                  onChange={(e) => {
                    setFormData({ ...formData, description: e.target.value });
                    setErrors({ ...errors, description: '' });
                  }}
                  rows={4}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent resize-none bg-white text-gray-900 placeholder-gray-500"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  placeholder=""
                  maxLength={500}
                />
              </div>
              {formData.description && (
                <p className="text-xs text-slate-600 mt-1 font-medium">
                  {formData.description.length}/500 characters
                </p>
              )}
            </div>

            {/* Active Status */}
            <div>
              <label className="admin-form-label">
                Status
              </label>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: true })}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${formData.is_active
                    ? 'bg-green-100 text-green-800 border-2 border-green-400'
                    : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'
                    }`}
                >
                  <CheckCircle size={20} />
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: false })}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${!formData.is_active
                    ? 'bg-red-100 text-red-800 border-2 border-red-400'
                    : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'
                    }`}
                >
                  <XCircle size={20} />
                  Inactive
                </button>
              </div>
              <p className="text-slate-600 text-xs mt-2 font-medium">
                Inactive sites cannot be assigned to new users, but existing users will retain access.
              </p>
            </div>

            {/* New Fields Section */}
            <div className="border-t pt-6 mt-6">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">Additional Site Information</h3>

              {/* Timezone Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="admin-form-label">
                    Local Time Zone
                  </label>
                  <select
                    value={formData.local_time_zone}
                    onChange={(e) => setFormData({ ...formData, local_time_zone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                  >
                    <option value="">Select Local Time Zone</option>
                    {dropdowns.timezones.length > 0 ? dropdowns.timezones.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    )) : (
                      <option value="Asia/Kolkata">Asia/Kolkata</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="admin-form-label">
                    Timezone Preference
                  </label>
                  <select
                    value={formData.timezone_preference}
                    onChange={(e) => setFormData({ ...formData, timezone_preference: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                  >
                    <option value="">Select Timezone Preference</option>
                    {dropdowns.timezones.length > 0 ? dropdowns.timezones.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    )) : (
                      <option value="Asia/Kolkata">Asia/Kolkata</option>
                    )}
                  </select>
                </div>
              </div>

              {/* Location Coordinates */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="admin-form-label">
                    Latitude
                  </label>
                  <input
                    type="number"
                    step="0.0000001"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 font-medium"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    placeholder=""
                  />
                </div>
                <div>
                  <label className="admin-form-label">
                    Longitude
                  </label>
                  <input
                    type="number"
                    step="0.0000001"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 font-medium"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    placeholder=""
                  />
                </div>
                <div>
                  <label className="admin-form-label">
                    Altitude (meters)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.altitude}
                    onChange={(e) => setFormData({ ...formData, altitude: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 font-medium"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    placeholder=""
                  />
                </div>
              </div>

              {/* Plant Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="admin-form-label">
                    PPA Tariff
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    value={formData.ppa_tariff}
                    onChange={(e) => setFormData({ ...formData, ppa_tariff: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 font-medium"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    placeholder=""
                  />
                </div>
                <div>
                  <label className="admin-form-label">
                    Plant Code
                  </label>
                  <select
                    value={formData.plant_code}
                    onChange={(e) => setFormData({ ...formData, plant_code: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                  >
                    <option value="">Select Plant Type</option>
                    {dropdowns.plant_codes.length > 0 ? (
                      dropdowns.plant_codes.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="0">Solar</option>
                        <option value="1">Wind</option>
                        <option value="2">Hybrid</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="admin-form-label">
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                  >
                    <option value="">Select Currency</option>
                    {dropdowns.currencies.length > 0 ? dropdowns.currencies.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    )) : (
                      <option value="INR">INR</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="admin-form-label">
                    Plant Commissioning Date
                  </label>
                  <input
                    type="date"
                    value={formData.plant_commissioning_date}
                    onChange={(e) => setFormData({ ...formData, plant_commissioning_date: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 font-medium"
                  />
                </div>
                <div>
                  <label className="admin-form-label">
                    Data Resolution
                  </label>
                  <select
                    value={formData.data_resolution}
                    onChange={(e) => setFormData({ ...formData, data_resolution: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                  >
                    <option value="">Select Resolution</option>
                    {dropdowns.data_resolutions && dropdowns.data_resolutions.length > 0 ? (
                      dropdowns.data_resolutions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="1m">1 Minute</option>
                        <option value="5m">5 Minutes</option>
                        <option value="10m">10 Minutes</option>
                        <option value="15m">15 Minutes</option>
                        <option value="30m">30 Minutes</option>
                        <option value="1h">1 Hour</option>
                        <option value="1d">1 Day</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              {/* Site Manager Information */}
              <div className="border-t pt-6 mt-6">
                <h4 className="text-sm font-semibold text-slate-800 mb-4">Site Manager Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="admin-form-label">
                      Site Manager Name
                    </label>
                    <input
                      type="text"
                      value={formData.site_manager_name}
                      onChange={(e) => setFormData({ ...formData, site_manager_name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 font-medium"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                      placeholder=""
                    />
                  </div>
                  <div>
                    <label className="admin-form-label">
                      Site Manager Email
                    </label>
                    <input
                      type="email"
                      value={formData.site_manager_email}
                      onChange={(e) => setFormData({ ...formData, site_manager_email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 font-medium"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                      placeholder=""
                    />
                  </div>
                  <div>
                    <label className="admin-form-label">
                      Site Manager Phone No.
                    </label>
                    <input
                      type="tel"
                      value={formData.site_manager_phone_no}
                      onChange={(e) => setFormData({ ...formData, site_manager_phone_no: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 font-medium"
                      style={{ color: '#111827', backgroundColor: '#ffffff' }}
                      placeholder=""
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/admin/sites')}
                className="admin-button admin-button-ghost sm:flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || siteNameValidation.isChecking || siteCodeValidation.isChecking || capacityValidation.isChecking}
                className="admin-button admin-button-primary sm:flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Create Site
                  </>
                )}
              </button>
            </div>
          </form>
          </div>
        </motion.section>
      </div>

      {/* Table Schema Customization Modal */}
      <TableSchemaModal
        isOpen={activeSchemaModal !== null}
        tableType={activeSchemaModal}
        initialColumns={activeSchemaModal === 'livedaycons' ? livedayconsColumns : livestringinvColumns}
        onSave={(updated) => {
          if (activeSchemaModal === 'livedaycons') {
            setLivedayconsColumns(updated);
          } else if (activeSchemaModal === 'livestringinv') {
            setLivestringinvColumns(updated);
          }
        }}
        onClose={() => setActiveSchemaModal(null)}
      />
    </div>
  );
};

export default SiteCreate;

